import { useEffect, useState } from "react";
import axios from "axios";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { Users, Layers, GraduationCap, Building2, ChevronDown, Lock } from "lucide-react"; 
import { API_BASE_URL } from "../infra/apiConfig";

// Paleta de cores
const COLORS = [
  "#4F46E5", "#10B981", "#F59E0B", "#EF4444",
  "#3B82F6", "#6366F1", "#EC4899", "#8B5CF6",
];

// ==== Componentes de gráficos ====

// ATUALIZADO: Componente de Gráfico de Pizza (com 'cx' e 'cy' ajustados)
function CardGraficoPizza({ title, data }) {
  return (
    <div className="bg-white rounded-2xl shadow p-6 border border-gray-100">
      <h3 className="text-lg font-semibold text-indigo-700 mb-4 text-center">{title}</h3>
      {data.length === 0 ? (
        <p className="text-center text-gray-500">Sem dados disponíveis.</p>
      ) : (
        <ResponsiveContainer width="100%" height={350}>
          <PieChart>
            <Pie
              dataKey="value"
              data={data}
              cx="50%"
              cy="45%"
              outerRadius={90}
              fill="#8884d8"
              label={(entry) => entry.value}
            >
              {data.map((_, idx) => (
                <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
              ))}
            </Pie>

            <Tooltip formatter={(value, name) => [value, name]} />

            {/* ✅ Legenda abaixo e horizontal */}
            <Legend
              layout="horizontal"
              verticalAlign="bottom"
              align="center"
              wrapperStyle={{
                fontSize: "12px",
                lineHeight: "20px",
                marginTop: "10px",
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function CardGraficoColuna({ title, data }) {
  return (
    <div className="bg-white rounded-2xl shadow p-6 border border-gray-100">
      <h3 className="text-lg font-semibold text-indigo-700 mb-4 text-center">{title}</h3>
      {data.length === 0 ? (
        <p className="text-center text-gray-500">Sem dados disponíveis.</p>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Legend />
            <Bar dataKey="value" name="Total" fill="#4F46E5" />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ==== Componentes auxiliares ====
function ItemEquipe({ equipe }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-gray-200 rounded-lg mb-3 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center p-4 bg-indigo-50 hover:bg-indigo-100 text-left"
      >
        <span className="font-semibold text-indigo-800 text-lg">{equipe.nome_equipe}</span>
        <ChevronDown
          className={`transform transition-transform ${isOpen ? "rotate-180" : ""}`}
          size={20}
        />
      </button>

      {isOpen && (
        <div className="p-4 bg-white divide-y divide-gray-100">
          <div className="py-4">
            <h4 className="text-md font-semibold text-gray-700 mb-2">Líder da Equipe</h4>
            <DetalhePessoa pessoa={equipe} prefixo="lider_" />
          </div>

          <div className="py-4">
            <h4 className="text-md font-semibold text-gray-700 mb-2">
              Membros ({equipe.membros.length})
            </h4>
            {equipe.membros.length === 0 ? (
              <p className="text-sm text-gray-500">Nenhum membro cadastrado.</p>
            ) : (
              <div className="space-y-3">
                {equipe.membros.map((membro) => (
                  <DetalhePessoa key={membro.id} pessoa={membro} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ==== NOVO COMPONENTE: ItemProjeto ====
function ItemProjeto({ projeto }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-gray-200 rounded-lg mb-4 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center p-4 bg-green-50 hover:bg-green-100 text-left"
      >
        <div>
          <span className="font-semibold text-green-800 text-lg block">
            {projeto.nome_iniciativa}
          </span>
          <span className="text-sm text-green-600">
            Equipe: {projeto.nome_equipe} | Líder: {projeto.lider_nome}
          </span>
        </div>
        <ChevronDown
          className={`transform transition-transform ${isOpen ? "rotate-180" : ""}`}
          size={20}
        />
      </button>

      {isOpen && (
        <div className="p-4 bg-white divide-y divide-gray-100">
          {/* Informações Básicas */}
          <div className="py-3">
            <h4 className="text-md font-semibold text-gray-700 mb-2">Informações do Projeto</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p><span className="font-medium">Área Temática:</span> {projeto.area_tematica || '—'}</p>
                <p><span className="font-medium">Equipe:</span> {projeto.nome_equipe || '—'}</p>
                <p><span className="font-medium">Líder:</span> {projeto.lider_nome || '—'}</p>
              </div>
              <div>
                <p><span className="font-medium">ID do Projeto:</span> {projeto.id}</p>
                <p><span className="font-medium">ID da Equipe:</span> {projeto.equipe_id}</p>
              </div>
            </div>
          </div>

          {/* Problema e Objetivo */}
          {projeto.problema_objetivo && (
            <div className="py-3">
              <h4 className="text-md font-semibold text-gray-700 mb-2">Problema e Objetivo</h4>
              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded border">
                {projeto.problema_objetivo}
              </p>
            </div>
          )}

          {/* Público Beneficiário */}
          {projeto.publico_beneficiario && (
            <div className="py-3">
              <h4 className="text-md font-semibold text-gray-700 mb-2">Público Beneficiário</h4>
              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded border">
                {projeto.publico_beneficiario}
              </p>
            </div>
          )}

          {/* Caráter de Inovação */}
          {projeto.carater_inovacao && (
            <div className="py-3">
              <h4 className="text-md font-semibold text-gray-700 mb-2">Caráter de Inovação</h4>
              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded border">
                {projeto.carater_inovacao}
              </p>
            </div>
          )}

          {/* Impactos Previstos */}
          {projeto.impactos_previstos && (
            <div className="py-3">
              <h4 className="text-md font-semibold text-gray-700 mb-2">Impactos Previstos</h4>
              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded border">
                {projeto.impactos_previstos}
              </p>
            </div>
          )}

          {/* Potencial de Execução */}
          {projeto.potencial_execucao && (
            <div className="py-3">
              <h4 className="text-md font-semibold text-gray-700 mb-2">Potencial de Execução</h4>
              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded border">
                {projeto.potencial_execucao}
              </p>
            </div>
          )}

          {/* Contribuição da Parceria */}
          {projeto.contribuicao_parceria && (
            <div className="py-3">
              <h4 className="text-md font-semibold text-gray-700 mb-2">Contribuição da Parceria</h4>
              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded border">
                {projeto.contribuicao_parceria}
              </p>
            </div>
          )}

          {/* Etapas do Projeto */}
          <div className="py-3">
            <h4 className="text-md font-semibold text-gray-700 mb-2">
              Etapas do Projeto ({projeto.etapas.length})
            </h4>
            {projeto.etapas.length === 0 ? (
              <p className="text-sm text-gray-500">Nenhuma etapa cadastrada.</p>
            ) : (
              <div className="space-y-2">
                {projeto.etapas.map((etapa, index) => (
                  <div key={etapa.id} className="flex items-start p-3 bg-blue-50 rounded border">
                    <span className="font-medium text-blue-700 min-w-16">Mês {etapa.mes}:</span>
                    <span className="text-sm text-gray-700 ml-2">{etapa.acao}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DetalhePessoa({ pessoa, prefixo = "" }) {
  const nome = pessoa[prefixo + 'nome'] || pessoa['nome_completo'];

  if (prefixo === 'lider_' && !nome) {
    return (
      <div className="p-3 bg-gray-50 rounded-md border text-sm">
        <p className="font-semibold text-gray-500 italic">Equipe sem líder associado.</p>
      </div>
    );
  }

  const cpf = pessoa[prefixo + 'cpf'] || pessoa['cpf'];
  const idade = pessoa[prefixo + 'idade'] ?? pessoa['idade'];
  const escola = pessoa[prefixo + 'escola'] || pessoa['escola'];
  const telefone = pessoa[prefixo + 'telefone'] || pessoa['telefone'];
  const serie = pessoa[prefixo + 'serie'] || pessoa['serie'];
  const categoria = pessoa[prefixo + 'categoria'] || pessoa['categoria'];

  return (
    <div className="p-3 bg-gray-50 rounded-md border text-sm">
      <p className="font-semibold text-gray-800">{nome}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-gray-600 mt-1">
        <p><span className="font-medium">CPF:</span> {cpf || '—'}</p>
        {(idade !== null && idade !== undefined) ? (
          <p><span className="font-medium">Idade:</span> {idade} anos</p>
        ) : (
          <p><span className="font-medium">Idade:</span> —</p>
        )}
        <p><span className="font-medium">Telefone:</span> {telefone || '—'}</p>
        <p><span className="font-medium">Escola:</span> {escola || '—'}</p>
        <p><span className="font-medium">Série:</span> {serie || '—'}</p>
      </div>
      <p className="mt-1"><span className="font-medium">Categoria:</span> {categoria || '—'}</p>
    </div>
  );
}

// ==== DASHBOARD ====
export default function DashboardPublico() {
  // === NOVOS ESTADOS ===
  const [autenticado, setAutenticado] = useState(false);
  const [senha, setSenha] = useState("");
  const [erroLogin, setErroLogin] = useState("");
  
  // === ESTADOS ANTIGOS ===
  const [data, setData] = useState(null);
  // 'loading' agora começa como false. Só fica true durante a tentativa de login.
  const [loading, setLoading] = useState(false); 
  
  // O useEffect(fetchData) foi removido. A busca será feita no 'handleLogin'.

  // === NOVA FUNÇÃO DE LOGIN ===
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErroLogin("");

    try {
      const res = await axios.get(`${API_BASE_URL}/dashboard-publico`, {
        headers: {
          // É aqui que enviamos a senha para o backend
          'Authorization': `Bearer ${senha}` 
        }
      });
      
      // Se deu certo:
      setData(res.data);
      setAutenticado(true);

    } catch (err) {
      console.error("Erro ao autenticar no dashboard:", err);
      // O backend retorna 401 ou 403, caindo aqui.
      setErroLogin("Senha incorreta. Acesso negado.");
    } finally {
      setLoading(false);
    }
  };

  // === TELA DE LOGIN (MODAL) ===
  // Se o usuário NÃO estiver autenticado, mostra esta tela:
  if (!autenticado) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="p-8 bg-white rounded-2xl shadow-xl max-w-sm w-full border border-gray-200">
          <div className="flex flex-col items-center">
            <div className="p-3 bg-indigo-100 rounded-full text-indigo-600">
              <Lock size={32} />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mt-4">Acesso Restrito</h2>
            <p className="text-gray-500 text-sm mt-1 text-center">
              Por favor, insira a chave de acesso para visualizar o dashboard.
            </p>
          </div>

          <form onSubmit={handleLogin} className="mt-6">
            <div>
              <label htmlFor="senha-dashboard" className="block text-sm font-medium text-gray-700">
                Chave de Acesso
              </label>
              <input
                type="password"
                id="senha-dashboard"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            
            {erroLogin && (
              <p className="text-red-600 text-sm text-center mt-3">{erroLogin}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 mt-6 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-300"
            >
              {loading ? "Verificando..." : "Entrar"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // === TELA DO DASHBOARD (A PARTIR DAQUI, TUDO IGUAL) ===
  // Se 'autenticado' for true, o código abaixo é executado.
  // (Adicionei uma verificação de 'data' para o caso de algo dar errado)
  if (!data) return <div className="text-center mt-10 text-red-600">Erro ao carregar dados.</div>;

  const { totais, lideresTodos, membros, equipes, projetosPorArea, escolasTop, projetos } = data;

  // === Funções auxiliares ===
  
  // ATUALIZADO: Função contarCategorias (sem cortar o nome)
const contarCategorias = (pessoas) => {
  const categorias = [
    "EX ALUNO DO PASSAPORTE DIGITAL",
    "ALUNO ATUAL DO PASSAPORTE DIGITAL",
    "ESTUDANTE DA REDE DE ESCOLAS DO MUNICÍPIO DE LAGOA DE ITAENGA",
  ];

  const formatarNome = (nome) => {
    if (nome === "EX ALUNO DO PASSAPORTE DIGITAL")
      return "EX-ALUNO DO PASSAPORTE DIGITAL"; // com hífen
    return nome;
  };

  return categorias
    .map((cat) => ({
      name: formatarNome(cat),
      value: pessoas.filter((p) => p.categoria === cat).length,
    }))
    .filter((cat) => cat.value > 0);
};


  const contarIdades = (pessoas) => {
    const grupos = {};
    pessoas.forEach((p) => {
      if (p.idade === null || p.idade === undefined) return;
      grupos[p.idade] = (grupos[p.idade] || 0) + 1;
    });
    return Object.entries(grupos).map(([idade, total]) => ({
      name: `${idade} anos`,
      value: total,
    }));
  };

  // === Gráficos atualizados ===
  const lideresCategorias = contarCategorias(lideresTodos);
  const membrosCategorias = contarCategorias(membros);
  const lideresIdades = contarIdades(lideresTodos);
  const membrosIdades = contarIdades(membros);

  const projetosAreaFormatado = projetosPorArea.map(p => ({
    name: p.area_tematica,
    value: p.total
  }));

  const cardInfo = [
    { icon: <Users />, label: "Total de Pessoas", value: totais.totalPessoas },
    { icon: <Layers />, label: "Equipes", value: totais.totalEquipes },
    { icon: <GraduationCap />, label: "Projetos", value: projetos.length }, // 👈 Agora mostra o número real de projetos
    { icon: <Building2 />, label: "Escolas (Top 5)", value: escolasTop.length },
  ];

  return (
    <div className="p-6 space-y-8 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold text-indigo-700 text-center mb-8">
        Dashboard Inscrições Prêmio Inovação 📊
      </h1>

      {/* ==== Cards de Totais ==== */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cardInfo.map((item, idx) => (
          <div
            key={idx}
            className="bg-white shadow rounded-2xl p-4 flex flex-col items-center justify-center text-center border border-indigo-100"
          >
            <div className="text-indigo-600 mb-2">{item.icon}</div>
            <div className="text-2xl font-bold text-gray-800">{item.value}</div>
            <div className="text-sm text-gray-500">{item.label}</div>
          </div>
        ))}
      </div>

      {/* ==== Gráficos ==== */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
        <CardGraficoPizza title="Categorias dos Líderes" data={lideresCategorias} />
        <CardGraficoPizza title="Categorias dos Membros" data={membrosCategorias} />
        <CardGraficoPizza title="Projetos por Área Temática" data={projetosAreaFormatado} />
        <CardGraficoColuna title="Idades dos Líderes" data={lideresIdades} />
        <CardGraficoColuna title="Idades dos Membros" data={membrosIdades} />
      </div>

      {/* ==== Equipes ==== */}
      <div className="mt-12 bg-white rounded-2xl shadow p-6">
        <h2 className="text-2xl font-semibold text-indigo-700 mb-4">
          Equipes e seus Participantes 👥
        </h2>
        <div>
          {equipes.map((eq) => (
            <ItemEquipe key={eq.equipe_id} equipe={eq} />
          ))}
        </div>
      </div>

      {/* ==== NOVA SEÇÃO: Projetos Cadastrados ==== */}
      <div className="mt-12 bg-white rounded-2xl shadow p-6">
        <h2 className="text-2xl font-semibold text-green-700 mb-4">
          Projetos Cadastrados 🚀
        </h2>
        <div className="mb-4 text-sm text-gray-600">
          Total de {projetos.length} projeto(s) cadastrado(s)
        </div>
        <div>
          {projetos.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-4">
              Nenhum projeto cadastrado.
            </p>
          ) : (
            projetos.map((projeto) => (
              <ItemProjeto key={projeto.id} projeto={projeto} />
            ))
          )}
        </div>
      </div>

      {/* ==== Todos os Líderes ==== */}
      <div className="mt-12 bg-white rounded-2xl shadow p-6">
        <h2 className="text-2xl font-semibold text-indigo-700 mb-4">
          Líderes Cadastrados 👤
        </h2>
        <div className="space-y-3">
          {lideresTodos.length === 0 ? (
            <p className="text-gray-500 text-sm">Nenhum líder cadastrado.</p>
          ) : (
            lideresTodos.map((lider) => (
              <div key={lider.id} className="p-3 border rounded-lg bg-gray-50">
                <p className="font-semibold text-gray-800">{lider.nome_completo}</p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Idade:</span> {lider.idade ?? '—'} |{" "}
                  <span className="font-medium">Categoria:</span> {lider.categoria || '—'} |{" "}
                  <span className="font-medium">Escola:</span> {lider.escola || '—'}
                </p>
                <p className="text-sm mt-1">
                  <span className="font-medium">Equipe:</span>{" "}
                  {lider.possui_equipe
                    ? "✅ Já lidera uma equipe"
                    : "❌ Ainda não possui equipe"}
                </p>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}